import axios, {AxiosInstance, AxiosStatic, AxiosError} from 'axios';
import axiosRetry, {exponentialDelay} from 'axios-retry';

import {setAppConnectionIssueAction, store, logoutActionSaga} from 'store';

const IDEMPOTENT_METHODS = ['get', 'head', 'options', 'put', 'delete'];

const isTimeout = (error: AxiosError<any>) =>
    error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';

/**
 * The request never got a usable answer from our API: no response at all
 * (offline, DNS, connection reset, timeout) or a proxy/gateway error.
 * These are what the reconnecting banner reports — a 4xx/500 means the
 * server is reachable and is the caller's problem to surface.
 */
export const isConnectionError = (error: AxiosError<any>) => {
    if (axios.isCancel(error)) return false;
    const status = error?.response?.status;
    return !status || status === 502 || status === 503 || status === 504;
};

const setConnectionIssue = (connectionIssue: boolean) => {
    if (store.getState()?.app?.connectionIssue !== connectionIssue)
        store.dispatch(setAppConnectionIssueAction(connectionIssue));
};

export const retryInstance = (instance: AxiosStatic | AxiosInstance) => {
    axiosRetry(instance, {
        retries: 3,
        // ** ~1s, 2s, 4s (+ jitter), or longer if the server sends Retry-After
        retryDelay: (retryCount: number, error: AxiosError<any>) =>
            exponentialDelay(retryCount, error, 500),
        // ** Give every attempt the full timeout. Without this, the timeout is
        // ** a budget shared by all attempts, so a slow first response leaves
        // ** nothing for a retry.
        shouldResetTimeout: true,

        onRetry: (retryCount: number, error: AxiosError<any>) => {
            // ** A single blip that recovers on the first retry shouldn't
            // ** flash the banner.
            if (retryCount >= 2 && isConnectionError(error))
                setConnectionIssue(true);
        },
        retryCondition: (error: AxiosError<any>) => {
            if (axios.isCancel(error)) return false;

            const errorStatus = error?.response?.status;
            // ** The logout request itself can 401 with an invalid/expired token; its own
            // ** saga already handles cleanup, so don't re-dispatch logout here or it loops forever.
            const isLogoutRequest = error?.config?.url?.includes('logout');

            if (
                errorStatus === 401 &&
                error?.response?.data?.detail?.includes('token') &&
                !isLogoutRequest
            ) {
                store.dispatch(logoutActionSaga());
                return false;
            }

            const method = error?.config?.method?.toLowerCase() ?? 'get';
            if (IDEMPOTENT_METHODS.includes(method)) {
                return (
                    !errorStatus || errorStatus === 429 || errorStatus >= 500
                );
            }

            // ** POST/PATCH: only retry when the request can't have been
            // ** processed. A timeout or 504 may have reached the API and
            // ** still be running, so retrying could e.g. book a session twice.
            if (!errorStatus) return !isTimeout(error);
            return (
                errorStatus === 429 ||
                errorStatus === 502 ||
                errorStatus === 503
            );
        },
    });

    // ** Registered after axios-retry, so it sees each request's final outcome.
    instance.interceptors.response.use(
        response => {
            setConnectionIssue(false);
            return response;
        },
        (error: AxiosError<any>) => {
            if (isConnectionError(error)) setConnectionIssue(true);
            else if (error?.response) setConnectionIssue(false);
            return Promise.reject(error);
        },
    );
};
